const API_BASE = "https://irene-api.heysalad.app"

function getToken(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|; )irene_token=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

export function setToken(token: string) {
  document.cookie = `irene_token=${encodeURIComponent(token)}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`
}

export function clearToken() {
  document.cookie = "irene_token=; path=/; max-age=0"
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json"
  }

  let res: Response
  try {
    console.log("[v0] API request:", options.method || "GET", `${API_BASE}${path}`)
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      mode: "cors",
    })
    console.log("[v0] API response status:", res.status, path)
  } catch (networkError) {
    // Network-level failure: CORS blocked, DNS failure, offline, etc.
    console.error("[v0] API network error:", path, networkError)
    throw new ApiError(
      "Network error: Unable to reach the server. Please check your connection.",
      0,
      { originalError: String(networkError) }
    )
  }

  let data: Record<string, unknown>
  try {
    data = await res.json()
  } catch {
    // Response was not valid JSON
    if (!res.ok) {
      throw new ApiError(
        `Server returned ${res.status} ${res.statusText}`,
        res.status,
        null
      )
    }
    // 2xx but non-JSON (shouldn't happen, but handle gracefully)
    return {} as T
  }

  if (!res.ok) {
    throw new ApiError(
      (data.message as string) || (data.error as string) || `Request failed (${res.status})`,
      res.status,
      data
    )
  }

  return data as T
}

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.data = data
  }
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface User {
  id: string
  email: string
  name: string
  company_name: string | null
  company_stage: string | null
  industry: string | null
  plan: string
  created_at: string
}

interface AuthResponse {
  success: boolean
  data: {
    user: User
    session_token: string
  }
}

export async function register(body: {
  email: string
  password: string
  name: string
  company_name?: string
  company_stage?: string
  industry?: string
}) {
  const res = await request<AuthResponse>("/auth/register", {
    method: "POST",
    body: JSON.stringify(body),
  })
  setToken(res.data.session_token)
  return res.data
}

export async function login(email: string, password: string) {
  const res = await request<AuthResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  })
  setToken(res.data.session_token)
  return res.data
}

export async function logout() {
  try {
    await request("/auth/logout", { method: "POST" })
  } catch {
    // Always clear token even if the API call fails
  }
  clearToken()
}

export async function forgotPassword(email: string) {
  const res = await request<{ success: boolean; message: string }>(
    "/auth/forgot-password",
    {
      method: "POST",
      body: JSON.stringify({ email }),
    }
  )
  return res
}

export async function verifyResetToken(token: string) {
  const res = await request<{ success: boolean; valid: boolean; message: string }>(
    `/auth/verify-reset-token/${token}`
  )
  return res
}

export async function resetPassword(token: string, password: string) {
  const res = await request<{ success: boolean; message: string }>(
    "/auth/reset-password",
    {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }
  )
  return res
}

export async function createCheckoutSession(priceId: string) {
  const res = await request<{ success: boolean; data: { sessionId: string; url: string } }>(
    "/auth/create-checkout-session",
    {
      method: "POST",
      body: JSON.stringify({ priceId }),
    }
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Investors
// ---------------------------------------------------------------------------

export interface ApiInvestor {
  id: string
  user_id: string
  name: string
  firm: string
  email: string | null
  stage: string
  type: string | null
  engagement_score: number
  investment_thesis: string | null
  tags: string | string[] | null
  created_at: string
  updated_at: string
}

export interface ApiInvestorNote {
  id: string
  investor_id: string
  content: string
  created_by: string
  created_at: string
}

interface InvestorsResponse {
  success: boolean
  data: ApiInvestor[]
}

interface InvestorDetailResponse {
  success: boolean
  data: ApiInvestor & { notes: ApiInvestorNote[] }
}

export async function getInvestors() {
  const res = await request<InvestorsResponse>("/investors")
  return res.data
}

export async function getInvestor(id: string) {
  const res = await request<InvestorDetailResponse>(`/investors/${id}`)
  return res.data
}

export async function createInvestor(body: {
  name: string
  firm: string
  email?: string
  stage?: string
  type?: string
  engagement_score?: number
  investment_thesis?: string
  tags?: string[]
}) {
  const res = await request<{ success: boolean; data: ApiInvestor }>(
    "/investors",
    { method: "POST", body: JSON.stringify(body) }
  )
  return res.data
}

export async function updateInvestor(
  id: string,
  body: Partial<{
    name: string
    firm: string
    email: string
    stage: string
    type: string
    engagement_score: number
    investment_thesis: string
    tags: string[]
  }>
) {
  const res = await request<{ success: boolean; data: ApiInvestor }>(
    `/investors/${id}`,
    { method: "PATCH", body: JSON.stringify(body) }
  )
  return res.data
}

export async function deleteInvestor(id: string) {
  await request(`/investors/${id}`, { method: "DELETE" })
}

export async function addInvestorNote(investorId: string, content: string) {
  const res = await request<{ success: boolean; data: ApiInvestorNote }>(
    `/investors/${investorId}/notes`,
    { method: "POST", body: JSON.stringify({ content }) }
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export interface ApiActivity {
  id: string
  user_id: string
  type: string
  title: string
  description: string | null
  investor_id: string | null
  created_at: string
}

interface ActivitiesResponse {
  success: boolean
  data: ApiActivity[]
}

export async function getActivities() {
  const res = await request<ActivitiesResponse>("/activities")
  return res.data
}

export async function createActivity(body: {
  type: string
  title: string
  description?: string
  investor_id?: string
}) {
  const res = await request<{ success: boolean; data: ApiActivity }>(
    "/activities",
    { method: "POST", body: JSON.stringify(body) }
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export interface ApiMetrics {
  total_investors: number
  investors_by_stage: Record<string, number>
  avg_engagement_score: number
  recent_activities: number
  total_analyses: number
  active_deal_rooms: number
}

interface MetricsResponse {
  success: boolean
  data: ApiMetrics
}

export async function getMetrics() {
  const res = await request<MetricsResponse>("/metrics")
  return res.data
}

// ---------------------------------------------------------------------------
// Deal Rooms
// ---------------------------------------------------------------------------

export interface ApiDealRoom {
  id: string
  user_id: string
  investor_id: string | null
  name: string
  status: string
  access_token: string
  created_at: string
  updated_at: string
}

interface DealRoomsResponse {
  success: boolean
  data: ApiDealRoom[]
}

export async function getDealRooms() {
  const res = await request<DealRoomsResponse>("/deal-rooms")
  return res.data
}

export async function createDealRoom(body: {
  name: string
  investor_id?: string
  status?: string
}) {
  const res = await request<{ success: boolean; data: ApiDealRoom }>(
    "/deal-rooms",
    { method: "POST", body: JSON.stringify(body) }
  )
  return res.data
}

export async function updateDealRoom(
  id: string,
  body: Partial<{ name: string; status: string }>
) {
  const res = await request<{ success: boolean; data: ApiDealRoom }>(
    `/deal-rooms/${id}`,
    { method: "PATCH", body: JSON.stringify(body) }
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Analyses
// ---------------------------------------------------------------------------

export interface ApiAnalysis {
  id: string
  user_id: string
  file_name: string
  file_url: string | null
  analysis_type: string
  overall_score: number
  overall_summary: string | null
  overall_strengths: string | null
  overall_weaknesses: string | null
  total_slides: number
  created_at: string
}

export interface ApiSlideAnalysis {
  id: string
  deck_analysis_id: string
  slide_number: number
  slide_title: string
  score: number
  strengths: string | null
  weaknesses: string | null
  feedback: string | null
}

interface AnalysesResponse {
  success: boolean
  data: ApiAnalysis[]
}

interface AnalysisDetailResponse {
  success: boolean
  data: ApiAnalysis & { slides: ApiSlideAnalysis[] }
}

export async function getAnalyses() {
  const res = await request<AnalysesResponse>("/analyses")
  return res.data
}

export async function getAnalysis(id: string) {
  const res = await request<AnalysisDetailResponse>(`/analyses/${id}`)
  return res.data
}

// ---------------------------------------------------------------------------
// Updates
// ---------------------------------------------------------------------------

export interface ApiUpdate {
  id: string
  user_id: string
  title: string
  content: string
  recipients: string | null
  sent_at: string | null
  created_at: string
}

interface UpdatesResponse {
  success: boolean
  data: ApiUpdate[]
}

export async function getUpdates() {
  const res = await request<UpdatesResponse>("/updates")
  return res.data
}

export async function createUpdate(body: {
  title: string
  content: string
  recipients?: string[]
}) {
  const res = await request<{ success: boolean; data: ApiUpdate }>(
    "/updates",
    { method: "POST", body: JSON.stringify(body) }
  )
  return res.data
}

// ---------------------------------------------------------------------------
// AI Searches
// ---------------------------------------------------------------------------

export interface ApiSearch {
  id: string
  user_id: string
  query: string
  filters: string | null
  results_count: number
  created_at: string
}

interface SearchesResponse {
  success: boolean
  data: ApiSearch[]
}

export async function getSearches() {
  const res = await request<SearchesResponse>("/searches")
  return res.data
}

export async function saveSearch(body: {
  query: string
  filters?: Record<string, unknown>
  results_count: number
}) {
  const res = await request<{ success: boolean; data: ApiSearch }>(
    "/searches",
    { method: "POST", body: JSON.stringify(body) }
  )
  return res.data
}
